import { useEffect, useMemo, useState } from "react";
import { Search, Users, Crown, ArrowDown, RotateCcw, ShieldOff, Smartphone, Gem, Receipt } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

function bytesHuman(n) {
  n = Number(n) || 0;
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(0)} MB`;
  return `${n} B`;
}

const PLAN_LABELS = { free: "Free", pro_standard: "Pro", pro_plus: "Pro Plus" };
const PLAN_COLORS = { free: "#E2E8F0", pro_standard: "#FDE047", pro_plus: "#86EFAC" };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [pwaStats, setPwaStats] = useState(null);
  const [grantUser, setGrantUser] = useState(null); // {user, plan, days, reason}
  const [paymentsUser, setPaymentsUser] = useState(null); // {user, txs}
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (filter) params.plan = filter;
      const r = await api.get("/admin/users", { params });
      setUsers(r.data.users || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Could not load users";
      const status = e?.response?.status;
      setLoadError({ status, detail });
      toast.error(`${status === 401 ? "Session expired — sign in again. " : ""}${detail}`, { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };

  const loadPwa = async () => {
    try {
      const r = await api.get("/admin/pwa/stats", { params: { days: 30 } });
      setPwaStats(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadPwa(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  // refetch on filter change (q triggers via Enter / search button)
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const cancel = async (u, immediate = false) => {
    const path = immediate ? "downgrade-now" : "cancel";
    const verb = immediate ? "Force-downgrade" : "Schedule cancel";
    if (!confirm(`${verb} ${u.email}? ${immediate ? "Pro features end immediately." : "User keeps Pro until pro_until."}`)) return;
    setBusyId(u.user_id);
    try {
      const r = await api.post(`/admin/users/${u.user_id}/${path}`);
      toast.success(r.data.message || "Done");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  };

  const reactivate = async (u) => {
    setBusyId(u.user_id);
    try {
      const r = await api.post(`/admin/users/${u.user_id}/reactivate`);
      toast.success(r.data.message || "Reactivated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  };

  const openGrant = (u) => setGrantUser({ user: u, plan: "pro_plus", days: 30, reason: "" });
  const submitGrant = async () => {
    if (!grantUser) return;
    const { user: u, plan, days, reason } = grantUser;
    setBusyId(u.user_id);
    try {
      const r = await api.post(`/admin/users/${u.user_id}/grant-pro`, { plan, days: Number(days), reason });
      toast.success(r.data.message || "Granted");
      setGrantUser(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  };

  const openPayments = async (u) => {
    setBusyId(u.user_id);
    try {
      const r = await api.get(`/admin/users/${u.user_id}/payments`);
      setPaymentsUser({ user: u, txs: r.data.transactions || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  };

  const forceApply = async (session_id) => {
    if (!confirm("Force-apply this payment? Use only when you've verified in Stripe Dashboard that the payment is genuine.")) return;
    try {
      const r = await api.post(`/admin/payments/${session_id}/force-apply`);
      toast.success(r.data.message || "Applied");
      // refresh payments list + users
      if (paymentsUser) await openPayments(paymentsUser.user);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const summary = useMemo(() => {
    const free = users.filter(u => u.effective_plan === "free").length;
    const pro = users.filter(u => u.effective_plan === "pro_standard").length;
    const proPlus = users.filter(u => u.effective_plan === "pro_plus").length;
    const cancelling = users.filter(u => u.cancelled_at && u.effective_plan !== "free").length;
    return { free, pro, proPlus, cancelling };
  }, [users]);

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-6xl">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Admin</p>
        <h1 className="font-display text-4xl font-black mt-1 flex items-center gap-2">
          <Users className="w-8 h-8" strokeWidth={2.5}/> Users
        </h1>
        <p className="text-sm font-medium text-slate-600 mt-2">Manage subscriptions, audit storage, see install stats.</p>
      </header>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="nb-card p-4"><div className="text-xs font-bold uppercase tracking-widest">Free</div><div className="font-display text-3xl font-black mt-1">{summary.free}</div></div>
        <div className="nb-card p-4 bg-yellow-300"><div className="text-xs font-bold uppercase tracking-widest">Pro</div><div className="font-display text-3xl font-black mt-1">{summary.pro}</div></div>
        <div className="nb-card p-4 bg-emerald-300"><div className="text-xs font-bold uppercase tracking-widest">Pro Plus</div><div className="font-display text-3xl font-black mt-1">{summary.proPlus}</div></div>
        <div className="nb-card p-4 bg-amber-100"><div className="text-xs font-bold uppercase tracking-widest">Cancelling</div><div className="font-display text-3xl font-black mt-1">{summary.cancelling}</div></div>
        <div className="nb-card p-4 bg-sky-200">
          <div className="text-xs font-bold uppercase tracking-widest flex items-center gap-1"><Smartphone className="w-3 h-3" strokeWidth={2.5}/> Installs (30d)</div>
          <div className="font-display text-3xl font-black mt-1">{pwaStats?.last_n_days ?? "—"}</div>
          <div className="text-xs text-slate-700 mt-1">{pwaStats ? `${pwaStats.total} all-time` : ""}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <form
          className="relative flex-1 min-w-[240px]"
          onSubmit={(e) => { e.preventDefault(); load(); }}
        >
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={2.5}/>
          <input
            data-testid="admin-users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email or name (Enter to apply)"
            className="nb-input pl-9"
          />
        </form>
        <select
          data-testid="admin-users-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="nb-input max-w-[200px]"
        >
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="pro_standard">Pro</option>
          <option value="pro_plus">Pro Plus</option>
        </select>
      </div>

      {loading ? (
        <div className="nb-card p-12 text-center">Loading…</div>
      ) : loadError ? (
        <div className="nb-card p-8 text-center bg-pink-100" data-testid="admin-users-load-error">
          <p className="font-display text-xl font-bold">Couldn't load users</p>
          <p className="text-sm font-medium text-slate-700 mt-2">
            {loadError.status === 401
              ? "Your session has expired on this device. Tap below to sign in again — your users haven't gone anywhere."
              : loadError.status === 403
                ? "This account isn't authorised for the admin area. Sign in with your admin email."
                : `Server said: ${loadError.detail || "unknown error"}.`}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
            <button onClick={load} data-testid="admin-users-retry" className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3">
              <RotateCcw className="w-3 h-3" strokeWidth={2.5}/> Retry
            </button>
            {loadError.status === 401 && (
              <a href="/login" data-testid="admin-users-relogin" className="nb-btn-primary text-xs inline-flex items-center gap-1 py-2 px-3">
                Sign in again
              </a>
            )}
          </div>
        </div>
      ) : users.length === 0 ? (
        <div className="nb-card p-12 text-center">
          <p className="font-display text-xl font-bold">No users match.</p>
          <p className="text-xs text-slate-500 mt-2">Clear the search box or change the plan filter.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((u) => {
            const planEffective = u.effective_plan || u.plan || "free";
            const isCancelling = !!u.cancelled_at && planEffective !== "free";
            return (
              <div key={u.user_id} className="nb-card p-4 flex flex-wrap items-center gap-4" data-testid={`admin-user-${u.email}`}>
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-10 h-10 rounded-full border-2 border-slate-900"/>
                ) : (
                  <div className="w-10 h-10 rounded-full border-2 border-slate-900 bg-slate-200 grid place-items-center font-bold">{(u.name || u.email || "?").charAt(0).toUpperCase()}</div>
                )}
                <div className="flex-1 min-w-[160px]">
                  <div className="font-bold truncate">{u.name || u.email}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="nb-tag text-xs whitespace-nowrap" style={{ background: PLAN_COLORS[planEffective] }}>
                    {planEffective !== "free" && <Crown className="w-3 h-3 inline mr-1" strokeWidth={2.5}/>}
                    {PLAN_LABELS[planEffective]}
                  </span>
                  {isCancelling && <span className="nb-tag bg-amber-200 text-xs">Cancelling</span>}
                </div>
                <div className="text-xs text-slate-700 min-w-[120px]">
                  <div><strong>{bytesHuman(u.used_bytes)}</strong> stored</div>
                  {u.pro_until && (
                    <div className="text-slate-500">until {new Date(u.pro_until).toLocaleDateString()}</div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 ml-auto">
                  <button
                    data-testid={`admin-payments-${u.email}`}
                    onClick={() => openPayments(u)}
                    disabled={busyId === u.user_id}
                    className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3"
                    title="View payment history & force-apply stuck payments"
                  >
                    <Receipt className="w-3 h-3" strokeWidth={2.5}/> Payments
                  </button>
                  <button
                    data-testid={`admin-grant-${u.email}`}
                    onClick={() => openGrant(u)}
                    disabled={busyId === u.user_id}
                    className="nb-btn-primary text-xs inline-flex items-center gap-1 py-2 px-3"
                    title="Manually extend Pro — for stuck Stripe payments or comps"
                  >
                    <Gem className="w-3 h-3" strokeWidth={2.5}/> Grant Pro
                  </button>
                  {planEffective !== "free" && !isCancelling && (
                    <button
                      data-testid={`admin-cancel-${u.email}`}
                      onClick={() => cancel(u, false)}
                      disabled={busyId === u.user_id}
                      className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3"
                      title="Schedule cancellation at end of paid period"
                    >
                      <ArrowDown className="w-3 h-3" strokeWidth={2.5}/> Schedule cancel
                    </button>
                  )}
                  {isCancelling && (
                    <button
                      data-testid={`admin-reactivate-${u.email}`}
                      onClick={() => reactivate(u)}
                      disabled={busyId === u.user_id}
                      className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3"
                    >
                      <RotateCcw className="w-3 h-3" strokeWidth={2.5}/> Reactivate
                    </button>
                  )}
                  {planEffective !== "free" && (
                    <button
                      data-testid={`admin-force-${u.email}`}
                      onClick={() => cancel(u, true)}
                      disabled={busyId === u.user_id}
                      className="nb-btn-danger text-xs inline-flex items-center gap-1 py-2 px-3"
                      title="Immediate downgrade — for fraud / abuse cases"
                    >
                      <ShieldOff className="w-3 h-3" strokeWidth={2.5}/> Force downgrade
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Grant Pro modal */}
      {grantUser && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 grid place-items-center p-4" onClick={() => setGrantUser(null)} data-testid="grant-pro-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <Gem className="w-6 h-6" strokeWidth={2.5}/>
              <h2 className="font-display text-2xl font-black">Grant Pro</h2>
            </div>
            <p className="text-sm text-slate-600 mb-4">Extending plan for <strong>{grantUser.user.email}</strong>. This adds days on top of any existing pro_until.</p>
            <label className="block text-xs uppercase tracking-widest font-bold mb-1">Plan</label>
            <select
              data-testid="grant-pro-plan"
              value={grantUser.plan}
              onChange={(e) => setGrantUser({ ...grantUser, plan: e.target.value })}
              className="nb-input mb-3"
            >
              <option value="pro_plus">Pro Plus (8 GB, AI drafts)</option>
              <option value="pro_standard">Pro (4 GB)</option>
            </select>
            <label className="block text-xs uppercase tracking-widest font-bold mb-1">Days</label>
            <input
              data-testid="grant-pro-days"
              type="number" min={1} max={3650}
              value={grantUser.days}
              onChange={(e) => setGrantUser({ ...grantUser, days: e.target.value })}
              className="nb-input mb-3"
            />
            <label className="block text-xs uppercase tracking-widest font-bold mb-1">Reason (internal)</label>
            <input
              data-testid="grant-pro-reason"
              value={grantUser.reason}
              onChange={(e) => setGrantUser({ ...grantUser, reason: e.target.value })}
              placeholder="e.g. Stripe checkout didn't auto-apply"
              className="nb-input mb-4"
              maxLength={200}
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setGrantUser(null)} className="nb-btn-secondary" data-testid="grant-pro-cancel">Cancel</button>
              <button onClick={submitGrant} disabled={busyId} className="nb-btn-primary inline-flex items-center gap-2" data-testid="grant-pro-submit">
                <Gem className="w-4 h-4" strokeWidth={2.5}/> Grant
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payments history modal */}
      {paymentsUser && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 grid place-items-center p-4 overflow-y-auto" onClick={() => setPaymentsUser(null)} data-testid="payments-modal">
          <div className="nb-card p-6 w-full max-w-2xl bg-white max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="w-6 h-6" strokeWidth={2.5}/>
              <h2 className="font-display text-2xl font-black">Payments</h2>
            </div>
            <p className="text-sm text-slate-600 mb-4">{paymentsUser.user.email}</p>
            {paymentsUser.txs.length === 0 ? (
              <p className="text-sm text-slate-500">No payment transactions on record.</p>
            ) : (
              <div className="space-y-3">
                {paymentsUser.txs.map((tx) => (
                  <div key={tx.session_id} className="border-2 border-slate-900 rounded-md p-3 bg-slate-50" data-testid={`payment-${tx.session_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-xs font-mono break-all">{tx.session_id}</div>
                      <span className={`nb-tag text-xs ${tx.applied ? "bg-emerald-300" : tx.payment_status === "paid" ? "bg-amber-200" : "bg-slate-200"}`}>
                        {tx.applied ? "applied" : tx.payment_status || "pending"}
                      </span>
                    </div>
                    <div className="text-sm mt-1">
                      {tx.amount} {tx.currency?.toUpperCase()} · <strong>{tx.plan}</strong> · {new Date(tx.created_at).toLocaleString()}
                    </div>
                    {!tx.applied && (
                      <button
                        data-testid={`force-apply-${tx.session_id}`}
                        onClick={() => forceApply(tx.session_id)}
                        className="nb-btn-primary mt-2 text-xs inline-flex items-center gap-1 py-2 px-3"
                      >
                        Force-apply (verify in Stripe first)
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="text-right mt-4">
              <button onClick={() => setPaymentsUser(null)} className="nb-btn-secondary" data-testid="payments-close">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
